import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useLang } from "../../i18n/LangContext";
import { api, extractError } from "../../api/client";
import { JourneyProgressCard } from "./JourneyProgressCard";
import { DayList } from "./DayList";
import { DayDetail } from "./DayDetail";

export { L, formatDateID } from "./lib";

const EMPTY_DRAFT = { checklist: [], answers: {}, reflection: "", video: false, material: false };

export function DayJourney({ journeyId, onProgress }) {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: res } = await api.get(`/education/journeys/${journeyId}/days`);
      setData(res.data);
      onProgress?.(res.data);
    } catch (e) { toast.error(extractError(e).message); }
  }, [journeyId, onProgress]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (dayNumber) => {
    if (detail?.day_number === dayNumber) return setDetail(null);
    try {
      const { data: res } = await api.get(`/education/journeys/${journeyId}/days/${dayNumber}`);
      const p = res.data.progress || {};
      setDetail(res.data);
      setDraft({
        checklist: p.checklist_items || [],
        answers: {},
        reflection: p.reflection_text || "",
        video: !!p.video_completed,
        material: !!p.material_completed,
      });
    } catch (e) { toast.error(extractError(e).message); }
  };

  const completeDay = async () => {
    const quiz = detail.content.find((c) => c.type === "QUIZ");
    const body = {
      video_completed: detail.requirements.video ? draft.video : undefined,
      material_completed: detail.requirements.material ? draft.material : undefined,
      checklist_items: detail.requirements.checklist ? draft.checklist : undefined,
      reflection_text: detail.requirements.reflection ? draft.reflection : undefined,
      quiz_answers: quiz ? quiz.questions.map((_, i) => draft.answers[i] ?? -1) : undefined,
    };
    setBusy(true);
    try {
      const { data: res } = await api.post(`/education/journeys/${journeyId}/days/${detail.day_number}/progress`, body);
      if (res.data.progress?.day_completed) {
        toast.success(t("edu.dayCompleted"));
        setDetail(null);
      } else {
        toast.info(t("edu.saved"));
      }
      load();
    } catch (e) { toast.error(extractError(e).message); }
    finally { setBusy(false); }
  };

  if (!data) return <p className="text-slate-400">{t("common.loading")}</p>;

  return (
    <div className="space-y-5" data-testid={`day-journey-${journeyId}`}>
      <JourneyProgressCard data={data} />
      {detail ? (
        <DayDetail detail={detail} draft={draft} setDraft={setDraft} busy={busy}
          onComplete={completeDay} onClose={() => setDetail(null)} />
      ) : (
        <DayList days={data.days} onOpen={openDetail} />
      )}
    </div>
  );
}
